import type { AdminInvite } from '@/api/endpoints/admin-invites';
import { ScreenHeader } from '@/components/layout/screen-header';
import { useAppTheme } from '@/providers/theme-provider';
import Ionicons from '@expo/vector-icons/Ionicons';
import React, { useCallback, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  Text,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { AdminCreateInviteModal } from '../components/admin-create-invite-modal';
import { AdminInviteRow } from '../components/admin-invite-row';
import {
  useAdminDeleteInvite,
  useAdminInviteStats,
  useAdminListInvites,
  useAdminSendInviteEmail,
  useAdminUpdateInviteEmail,
} from '../hooks/use-admin-invites';

const PER_PAGE = 20;

type StatusFilter = 'all' | 'active' | 'used' | 'expired';

const FILTERS: { label: string; value: StatusFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Used', value: 'used' },
  { label: 'Expired', value: 'expired' },
];

const STAT_COLOR_CLASS: Record<string, string> = {
  Active: 'text-primary',
  Used: 'text-muted-foreground',
  Expired: 'text-error',
  Total: 'text-foreground',
};

export function AdminInvitesScreen() {
  const theme = useAppTheme();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const offset = (page - 1) * PER_PAGE;

  const { data: statsData } = useAdminInviteStats();
  const { data, isLoading, isFetching } = useAdminListInvites({
    limit: PER_PAGE,
    offset,
    status: statusFilter,
  });

  const sendEmailMutation = useAdminSendInviteEmail();
  const deleteMutation = useAdminDeleteInvite();
  const updateEmailMutation = useAdminUpdateInviteEmail();

  const invites = data?.invites ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PER_PAGE);

  const handleSendEmail = useCallback(
    (code: string) => sendEmailMutation.mutate(code),
    [sendEmailMutation],
  );

  const handleDelete = useCallback(
    (code: string) => deleteMutation.mutate(code),
    [deleteMutation],
  );

  const handleUpdateEmail = useCallback(
    (code: string, email: string | null) => updateEmailMutation.mutate({ code, email }),
    [updateEmailMutation],
  );

  const handleFilterChange = (filter: StatusFilter) => {
    setStatusFilter(filter);
    setPage(1);
  };

  const renderItem = useCallback(
    ({ item }: { item: AdminInvite }) => (
      <AdminInviteRow
        invite={item}
        onSendEmail={handleSendEmail}
        onDelete={handleDelete}
        onUpdateEmail={handleUpdateEmail}
      />
    ),
    [handleSendEmail, handleDelete, handleUpdateEmail],
  );

  return (
    <SafeAreaView className="flex-1 bg-background">
      <ScreenHeader title="Invite Codes" showBackButton />

      {statsData && (
        <View
          style={{
            flexDirection: 'row',
            paddingHorizontal: 16,
            paddingBottom: 12,
            gap: 8,
          }}
        >
          {[
            { label: 'Active', value: statsData.active },
            { label: 'Used', value: statsData.used },
            { label: 'Expired', value: statsData.expired },
            { label: 'Total', value: statsData.total },
          ].map((stat) => (
            <View
              key={stat.label}
              className="bg-surface"
              style={{
                flex: 1,
                borderRadius: 10,
                padding: 10,
                alignItems: 'center',
              }}
            >
              <Text
                className={STAT_COLOR_CLASS[stat.label]}
                style={{ fontSize: 18, fontWeight: '700' }}
              >
                {stat.value}
              </Text>
              <Text className="text-xs text-muted-foreground">{stat.label}</Text>
            </View>
          ))}
        </View>
      )}

      <View style={{ paddingHorizontal: 16, paddingBottom: 12 }}>
        <Pressable
          onPress={() => setShowCreateModal(true)}
          accessibilityRole="button"
          accessibilityLabel="Create new invite code"
          className="bg-primary"
          style={{
            borderRadius: 10,
            paddingVertical: 12,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          <Ionicons name="add" size={20} className="text-background" />
          <Text className="text-background" style={{ fontSize: 15, fontWeight: '600' }}>
            Create Invite
          </Text>
        </Pressable>
      </View>

      <View style={{ flexDirection: 'row', paddingHorizontal: 16, paddingBottom: 8, gap: 8 }}>
        {FILTERS.map((f) => (
          <Pressable
            key={f.value}
            onPress={() => handleFilterChange(f.value)}
            accessibilityRole="button"
            accessibilityState={{ selected: statusFilter === f.value }}
            className={statusFilter === f.value ? 'bg-primary' : 'bg-surface'}
            style={{
              flex: 1,
              paddingVertical: 6,
              borderRadius: 8,
              alignItems: 'center',
            }}
          >
            <Text
              className={statusFilter === f.value ? 'text-background' : 'text-foreground'}
              style={{ fontSize: 13, fontWeight: '600' }}
            >
              {f.label}
            </Text>
          </Pressable>
        ))}
      </View>

      <FlatList
        data={invites}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        contentContainerStyle={{ paddingBottom: 24 }}
        ListEmptyComponent={
          isLoading ? (
            <View style={{ paddingVertical: 40, alignItems: 'center' }}>
              <ActivityIndicator color={theme.colors.primary} />
            </View>
          ) : (
            <View style={{ paddingVertical: 40, alignItems: 'center' }}>
              <Text className="text-muted-foreground" style={{ fontSize: 15 }}>No invites found</Text>
            </View>
          )
        }
      />

      {totalPages > 1 && (
        <View
          className="border-t border-border"
          style={{
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            paddingVertical: 12,
            gap: 16,
          }}
        >
          <Pressable
            disabled={page <= 1}
            onPress={() => setPage((p) => Math.max(1, p - 1))}
            accessibilityRole="button"
            accessibilityLabel="Previous page"
            style={{ opacity: page <= 1 ? 0.4 : 1 }}
          >
            <Text className="text-primary" style={{ fontSize: 15, fontWeight: '600' }}>
              Previous
            </Text>
          </Pressable>
          <Text className="text-muted-foreground" style={{ fontSize: 14 }}>
            {page} / {totalPages}
          </Text>
          <Pressable
            disabled={page >= totalPages}
            onPress={() => setPage((p) => Math.min(totalPages, p + 1))}
            accessibilityRole="button"
            accessibilityLabel="Next page"
            style={{ opacity: page >= totalPages ? 0.4 : 1 }}
          >
            <Text className="text-primary" style={{ fontSize: 15, fontWeight: '600' }}>
              Next
            </Text>
          </Pressable>
          {isFetching && <ActivityIndicator size="small" color={theme.colors.primary} />}
        </View>
      )}

      <AdminCreateInviteModal
        visible={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />
    </SafeAreaView>
  );
}
